import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
const DURATION_SECONDS = 30;
const KEPT_HOLD_MS = 2400;
type CountdownRingProps = {
  /** Visual scale: the hero device, or the persistent corner marker. */
  size?: 'lg' | 'sm';
  duration?: number;
};

/**
 * The recognition device: a live 30-second countdown that empties, resolves to
 * "Promise given. Promise kept." and then starts over.
 */
export function CountdownRing({
  size = 'lg',
  duration = DURATION_SECONDS
}: CountdownRingProps) {
  const reduced = useReducedMotion();
  const [remaining, setRemaining] = useState(duration);
  const [kept, setKept] = useState(false);
  useEffect(() => {
    if (reduced) return undefined;
    if (kept) {
      const timeout = window.setTimeout(() => {
        setRemaining(duration);
        setKept(false);
      }, KEPT_HOLD_MS);
      return () => window.clearTimeout(timeout);
    }
    const interval = window.setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          setKept(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [kept, reduced, duration]);
  const isLarge = size === 'lg';
  const radius = 88;
  const circumference = 2 * Math.PI * radius;
  const progress = kept ? 1 : remaining / duration;
  return <div className={isLarge ? 'relative aspect-square w-full max-w-[min(36vw,58vh)]' : 'relative aspect-square w-[clamp(38px,4.2vw,78px)]'} role="timer" aria-live="off" aria-label={kept ? 'Promise given, promise kept' : `${remaining} seconds remaining`} data-id="element-4" data-mp="1p2aqyi9gm1dw">
      <svg viewBox="0 0 200 200" className="h-full w-full -rotate-90" data-id="element-5" data-mp="1a636dgu52xy3">
        <circle cx="100" cy="100" r={radius} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth={isLarge ? 4 : 8} data-id="element-6" data-mp="39hvz319w9h50" />
        <motion.circle cx="100" cy="100" r={radius} fill="none" stroke="var(--sv-green)" strokeWidth={isLarge ? 4 : 8} strokeLinecap="round" strokeDasharray={circumference} animate={{
        strokeDashoffset: circumference * (1 - progress)
      }} transition={{
        duration: reduced ? 0 : kept ? 0.5 : 1,
        ease: kept ? 'easeOut' : 'linear'
      }} style={{
        filter: isLarge ? 'drop-shadow(0 0 12px rgba(74,222,128,0.55))' : 'drop-shadow(0 0 6px rgba(74,222,128,0.5))'
      }} data-id="element-7" data-mp="12tvgswmgw4eu" />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center px-[8%] text-center" data-id="element-8" data-mp="1cjf5wk0rwui5">
        <AnimatePresence mode="wait" initial={false} data-id="element-9" data-mp="1gnd32p6jfv8k">
          {kept ? <motion.div key="kept" initial={reduced ? false : {
          opacity: 0,
          scale: 0.94
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={reduced ? undefined : {
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="flex flex-col items-center" data-id="element-10" data-mp="13qcpawxez8w">
              {isLarge ? <>
                  <span className="text-[clamp(14px,1.9vw,36px)] font-light leading-tight text-white/70" data-id="element-11" data-mp="7k9vlhflhotx">
                    Promise given.
                  </span>
                  <span className="text-[clamp(16px,2.3vw,44px)] font-semibold leading-tight text-[color:var(--sv-green)]" data-id="element-12" data-mp="3b0pbreq80pdd">
                    Promise kept.
                  </span>
                </> : <span className="text-[clamp(12px,1.3vw,24px)] font-semibold leading-none text-[color:var(--sv-green)]" data-id="element-13" data-mp="3ks3fjyp7gbcb">
                  ✓
                </span>}
            </motion.div> : <motion.div key="count" initial={reduced ? false : {
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={reduced ? undefined : {
          opacity: 0
        }} transition={{
          duration: 0.25
        }} className="flex flex-col items-center" data-id="element-14" data-mp="2s96apdfrbldz">
              <span className={isLarge ? 'text-[clamp(56px,10.5vw,182px)] font-semibold leading-none tracking-tight text-white tabular-nums' : 'text-[clamp(12px,1.25vw,23px)] font-semibold leading-none tracking-tight text-white tabular-nums'} data-id="element-15" data-mp="1ujruuu5bq0k6">
                {remaining}
              </span>
              {isLarge ? <span className="mt-[1vh] text-[clamp(9px,0.95vw,17px)] uppercase tracking-[0.4em] text-[color:var(--sv-green)]" data-id="element-16" data-mp="2ib74l7zfjk5g">
                  seconds
                </span> : null}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}